/* eslint-disable prefer-object-spread */
import { createSlice } from '@reduxjs/toolkit';
import i18next from 'i18next';
import axiosInstance from '../../../data/axiosInstance/axiosInstance';
import executor from '../../../data/axiosInstance/executor';
import { messaggioErrore } from '../../../views/configuratoreRiserve/configuratoreRiserveApi';
import { esitoHttp, isEsitoOk, messaggioDelBe, VALIDA_FINO_A_4XX } from '../../../utils/esitoHttp';

const API_URL = `${process.env.REACT_APP_API_SINISTRI_URL}`;

const initialState = {
  // Loader della dashboard (griglia, Cerca, Pulisci): acceso finche' lista O dettaglio sono in corso. Lo derivano
  // i due flag sotto; setLoaderCatastrofali resta per chi lo imposta direttamente.
  loaderCatastrofali: false,
  caricamentoListaEventi: false,
  caricamentoDettaglioEvento: false,
  // `{messaggio}` quando il dettaglio dell'evento scelto non si carica (il dettaglio precedente si svuota); null se no.
  erroreDettaglioEvento: null,
  loaderGrigliaSinistri: false,
  listaEventiCatastrofali: [],
  singleEventoCatastrofali: {},
  selectEventValue: null,
  listaSinitsriCatastrofali: [],
  // Righe task dei sinistri dell'evento, recuperate dal BE per id (OSSQ-2381):
  // NON si filtra piu' la griglia lavorabili lato client, che dalla paginazione
  // server-side in poi contiene solo la pagina corrente.
  taskCatastrofali: [],
  // CSP-2788: bozze di evento proposte dal provider (hazard.bozza_evento), viste dal coordinatore.
  loaderBozze: false,
  listaBozze: [],
  bozzaSelezionata: null,
  // CSP-2788 (solo per il test): l'utente e' fra quelle che il BFF elenca in configurazione con tutti i permessi del
  // tab eventi (GET /eventoCatastrofale/permessi-test). False finche' non risponde, e su errore.
  tuttiIPermessiTest: false,
};

export const EventiCatastrofaliSlice = createSlice({
  name: 'eventiCatastrofali',
  initialState,
  reducers: {
    setLoaderCatastrofali: (state, action) => {
      state.loaderCatastrofali = action.payload;
    },
    setCaricamentoListaEventi: (state, action) => {
      state.caricamentoListaEventi = Boolean(action.payload);
      state.loaderCatastrofali = state.caricamentoListaEventi || state.caricamentoDettaglioEvento;
    },
    setCaricamentoDettaglioEvento: (state, action) => {
      state.caricamentoDettaglioEvento = Boolean(action.payload);
      state.loaderCatastrofali = state.caricamentoListaEventi || state.caricamentoDettaglioEvento;
    },
    setErroreDettaglioEvento: (state, action) => {
      state.erroreDettaglioEvento = action.payload || null;
    },
    setLoaderGrigliaSinistri: (state, action) => {
      state.loaderGrigliaSinistri = action.payload;
    },
    getListEvents: (state, action) => {
      state.listaEventiCatastrofali = action.payload;
    },
    setListaSinistriCatastrofali: (state, action) => {
      state.listaSinitsriCatastrofali = action.payload;
    },
    setTaskCatastrofali: (state, action) => {
      state.taskCatastrofali = action.payload || [];
    },
    setSelectEventValue : (state, action) => {
      state.selectEventValue = action.payload;
    },
    setSingleEventoCatastrofali: (state, action) => {
      state.singleEventoCatastrofali = action.payload;
    },
    setLoaderBozze: (state, action) => {
      state.loaderBozze = action.payload;
    },
    setListaBozze: (state, action) => {
      state.listaBozze = action.payload || [];
    },
    setBozzaSelezionata: (state, action) => {
      state.bozzaSelezionata = action.payload || null;
    },
    // CSP-2788 T18: dopo il PUT dei dati riass il "Codice riass" del dettaglio (stesso campo dell'anagrafica,
    // codice_evento_riassicurazione) si allinea senza rileggere l'evento; un altro evento selezionato non si tocca.
    aggiornaCodiceRiassSelezionato: (state, action) => {
      const { eventoId, codiceEventoRiass } = action.payload || {};
      if (state.singleEventoCatastrofali?.id === eventoId) {
        state.singleEventoCatastrofali.codiceEventoRiassicurazione = codiceEventoRiass ?? null;
      }
    },
    setTuttiIPermessiTest: (state, action) => {
      state.tuttiIPermessiTest = action.payload === true;
    },
  },
});

export const {
  setLoaderCatastrofali,
  setCaricamentoListaEventi,
  setCaricamentoDettaglioEvento,
  setErroreDettaglioEvento,
  setLoaderGrigliaSinistri,
  getListEvents,
  setListaSinistriCatastrofali,
  setTaskCatastrofali,
  setSelectEventValue,
  setSingleEventoCatastrofali,
  setLoaderBozze,
  setListaBozze,
  setBozzaSelezionata,
  aggiornaCodiceRiassSelezionato,
  setTuttiIPermessiTest,
} = EventiCatastrofaliSlice.actions;

// Senza un body del BE (rete giu', BFF irraggiungibile, 5xx vuoto) l'interceptor di axiosInstance
// rigetta con questo testo fisso; un Error e' invece un errore JavaScript. Nessuno dei due e' un
// messaggio per l'utente: al loro posto si mostra un testo generico tradotto.
const ERRORE_INTERCEPTOR_SENZA_BODY = 'Something went wrong';
const CHIAVE_ERRORE_RETE = 'layouts.components.eventiCatastrofali.erroreRete';

const erroreSenzaBodyDelBe = (err) => err === ERRORE_INTERCEPTOR_SENZA_BODY || err instanceof Error;

/** Testo per l'utente da un errore rigettato dall'interceptor: il messaggio del BE, o quello generico tradotto. */
const messaggioDaErrore = (err) => (erroreSenzaBodyDelBe(err) ? i18next.t(CHIAVE_ERRORE_RETE) : messaggioErrore(err));

// CSP-2788 (review n.9): cercando o pulendo in fretta partono piu' GET della lista; conta solo l'ultima (lista e
// loader), anche se la risposta di una precedente arriva dopo (come fetchListaBozze).
let ultimaRichiestaListaEventi = 0;

export const fetchListaEventiCatastrofali = (filtri = {}) => async (dispatch) => {
  ultimaRichiestaListaEventi += 1;
  const richiesta = ultimaRichiestaListaEventi;
  dispatch(getListEvents([]));
  dispatch(setCaricamentoListaEventi(true));

  const params = new URLSearchParams();
  if (filtri.tipo) params.append('tipo', filtri.tipo);
  if (filtri.data) params.append('data', filtri.data);
  if (filtri.nazione) params.append('nazione', filtri.nazione);
  if (filtri.regione) params.append('regione', filtri.regione);
  if (filtri.comune) params.append('comune', filtri.comune);

  const queryString = params.toString();
  const API_URI_CALL = `${API_URL}/api/v1/bff/eventoCatastrofale/lista${
    queryString ? `?${queryString}` : ''
  }`;

  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);
  if (richiesta !== ultimaRichiestaListaEventi) return { superata: true };

  dispatch(setCaricamentoListaEventi(false));
  if (err) return true;
  return dispatch(getListEvents(res.data));
};

// CSP-2788 (review n.2): aprendo A e poi B, solo la GET di B aggiorna il dettaglio, anche se quella di A arriva dopo
// (o fallisce dopo). Il dettaglio a video e le sezioni sotto (sinistri, riass) devono riferirsi allo stesso evento.
let ultimaRichiestaDettaglioEvento = 0;

export const fetchSingleEventById = (id) => async (dispatch) => {
  ultimaRichiestaDettaglioEvento += 1;
  const richiesta = ultimaRichiestaDettaglioEvento;
  dispatch(setErroreDettaglioEvento(null));
  dispatch(setCaricamentoDettaglioEvento(true));
  const API_URI_CALL = `${API_URL}/api/v1/bff/eventoCatastrofale/${id}`;

  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);
  if (richiesta !== ultimaRichiestaDettaglioEvento) return { superata: true };

  console.log('res', res);
  console.log('err', err);
  dispatch(setCaricamentoDettaglioEvento(false));
  if (err) {
    // Il dettaglio dell'evento precedente non resta a video: si svuota e si dice perche'.
    dispatch(setSingleEventoCatastrofali({}));
    dispatch(setErroreDettaglioEvento({ messaggio: messaggioDaErrore(err) || null }));
    return true;
  }

  return dispatch(setSingleEventoCatastrofali(res.data));
};

// ---------------------------------------------------------------------------
// CSP-2788 - Bozze ed eventi del coordinatore (EventoCatastrofaleCoordinatoreController del BFF).
// Le scritture restituiscono { esito, data } oppure { esito: false, messaggio } col testo del BE
// (400 con l'elenco dei campi, 403, 404, 409), da mostrare all'utente cosi' com'e'.
// ---------------------------------------------------------------------------

const BASE_EVENTO = `${API_URL}/api/v1/bff/eventoCatastrofale`;

const esitoChiamata = async (chiamata) => {
  const [res, err] = await executor.executeAsync(chiamata);
  if (err) {
    return { esito: false, messaggio: messaggioDaErrore(err) };
  }
  return { esito: true, data: res?.data };
};

// Cambiando stato in fretta partono piu' GET: solo l'ultima aggiorna lista e loader, anche se la
// risposta di una precedente arriva dopo (le altre tornano con `superata: true`).
let ultimaRichiestaBozze = 0;

/** Elenco bozze; senza stato il BE restituisce quelle in BOZZA. */
export const fetchListaBozze = (stato) => async (dispatch) => {
  ultimaRichiestaBozze += 1;
  const richiesta = ultimaRichiestaBozze;
  dispatch(setLoaderBozze(true));
  const query = stato ? `?${new URLSearchParams({ stato }).toString()}` : '';
  const risultato = await esitoChiamata(() => axiosInstance.get(`${BASE_EVENTO}/bozze${query}`));
  if (richiesta !== ultimaRichiestaBozze) return { ...risultato, superata: true };
  dispatch(setListaBozze(risultato.esito && Array.isArray(risultato.data) ? risultato.data : []));
  dispatch(setLoaderBozze(false));
  return risultato;
};

/**
 * Domini evento catastrofale (GET /eventoCatastrofale/domini del BFF, inoltro di os-sinistri
 * /riassicurazione/domini-evento-catastrofale): DominioEventoCatastrofaleDTO[], il tipo evento e'
 * `descrizioneEvento`. Di default solo gli attivi.
 */
export const fetchDominiEventoCatastrofale = (attivo = true) => async () =>
  esitoChiamata(() =>
    axiosInstance.get(`${BASE_EVENTO}/domini?${new URLSearchParams({ attivo }).toString()}`),
  );

export const fetchBozzaById = (id) => async (dispatch) => {
  const risultato = await esitoChiamata(() => axiosInstance.get(`${BASE_EVENTO}/bozze/${id}`));
  dispatch(setBozzaSelezionata(risultato.esito ? risultato.data : null));
  return risultato;
};

/** PUT della bozza: il BE risponde con la bozza aggiornata (campiManuali compresi). */
export const salvaBozza = (id, payload) => async (dispatch) => {
  const risultato = await esitoChiamata(() => axiosInstance.put(`${BASE_EVENTO}/bozze/${id}`, payload));
  if (risultato.esito) dispatch(setBozzaSelezionata(risultato.data));
  return risultato;
};

/**
 * CSP-2788 (solo per il test) - GET /eventoCatastrofale/permessi-test del BFF: `{tuttiIPermessi}` dice se l'utente e'
 * fra quelle in configurazione con tutti i permessi del tab eventi. Errore, 404 (endpoint assente) o risposta senza
 * il booleano true: false, il tab resta come oggi. Restituisce il valore salvato.
 */
export const fetchPermessiTestEventi = () => async (dispatch) => {
  const [res, err] = await executor.executeAsync(() => axiosInstance.get(`${BASE_EVENTO}/permessi-test`));
  const tuttiIPermessi = !err && res?.data?.tuttiIPermessi === true;
  dispatch(setTuttiIPermessiTest(tuttiIPermessi));
  return tuttiIPermessi;
};

/** Validazione: il BE crea l'evento in anagrafica e lo restituisce. */
export const validaBozza = (id) => async () =>
  esitoChiamata(() => axiosInstance.post(`${BASE_EVENTO}/bozze/${id}/valida`));

export const scartaBozza = (id) => async () =>
  esitoChiamata(() => axiosInstance.post(`${BASE_EVENTO}/bozze/${id}/scarta`));

/** "Crea nuovo evento" (EventoCatastrofaleFormDTO): 201 con l'evento creato. */
export const creaEventoCatastrofale = (form) => async () =>
  esitoChiamata(() => axiosInstance.post(BASE_EVENTO, form));

export const modificaEventoCatastrofale = (id, form) => async () =>
  esitoChiamata(() => axiosInstance.put(`${BASE_EVENTO}/${id}`, form));

// ---------------------------------------------------------------------------
// CSP-2788 (T16, D8-D10) - Sinistri dell'evento: liste "abbinati" e "potenziali" paginate dal server e
// consolida / disassocia v2 per {eventoId, sinistriId}. Tutto da os-sinistri via BFF (proxy as-is), senza
// CContact. Qui serve lo status (403 dei potenziali, 409 del disassocia): i 4xx tornano come risposta
// (VALIDA_FINO_A_4XX), non come errore dell'interceptor che ne perderebbe lo status.
// Esito: { esito, status, data, messaggio }; `nonAutorizzato` = 403 (l'utente non e' il coordinatore).
// ---------------------------------------------------------------------------

const esitoConStatus = async (chiamata) => {
  const { status, data } = await esitoHttp(chiamata);
  if (isEsitoOk(status)) return { esito: true, status, data, messaggio: null };
  const messaggio = status === null ? i18next.t(CHIAVE_ERRORE_RETE) : messaggioDelBe(data);
  return { esito: false, status, data, messaggio, nonAutorizzato: status === 403 };
};

/**
 * Una pagina di una delle due liste dell'evento.
 * @param {string} eventoId id dell'evento
 * @param {string} lista 'abbinati' | 'potenziali'
 * @param {string} query query string (page, size, sort, filtri) senza '?'
 */
export const fetchSinistriEvento = (eventoId, lista, query) => async () =>
  esitoConStatus(() =>
    axiosInstance.get(`${BASE_EVENTO}/${eventoId}/sinistri/${lista}${query ? `?${query}` : ''}`, {
      validateStatus: VALIDA_FINO_A_4XX,
    }),
  );

/** Consolida v2: `{eventoId, sinistriId}` -> `{eventoId, codiceEvento, aggiornati[], esclusi[]}`; 403 se non coordinatore. */
export const consolidaSinistriEvento = (richiesta) => async () =>
  esitoConStatus(() =>
    axiosInstance.put(`${BASE_EVENTO}/v2/consolida`, richiesta, { validateStatus: VALIDA_FINO_A_4XX }),
  );

/** Disassocia v2: come consolida; 409 se fra i selezionati c'e' un consolidato (nulla e' disassociato). */
export const disassociaSinistriEvento = (richiesta) => async () =>
  esitoConStatus(() =>
    axiosInstance.put(`${BASE_EVENTO}/v2/disassocia`, richiesta, { validateStatus: VALIDA_FINO_A_4XX }),
  );

/**
 * CSP-2788 (AFU) - "Carica da file": POST /eventoCatastrofale/import multipart con la parte `file` (.xlsx). Il BE crea
 * riga per riga e risponde 200 con { righe, create, errori: [{ riga, campo, messaggio }] }; 400 (file illeggibile o
 * intestazione errata) e 403 (non coordinatore) col messaggio del BFF. Il Content-Type col boundary lo mette axios.
 */
export const importaEventiCatastrofali = (file) => async () => {
  const corpo = new FormData();
  corpo.append('file', file);
  return esitoConStatus(() =>
    axiosInstance.post(`${BASE_EVENTO}/import`, corpo, { validateStatus: VALIDA_FINO_A_4XX }),
  );
};

// ---------------------------------------------------------------------------
// CSP-2788 (T14, D5) - Tendina "codice evento" del sinistro e "nessun evento" (EventoCatastrofaleAbbinamentoController
// del BFF, as-is da os-sinistri). Stessa forma d'esito delle liste: il 409 del "nessuno" (consolidato, oppure
// sinistro gia' abbinato e utente non Claims Coordinator, n.16) e il 400 portano il messaggio del BE.
// ---------------------------------------------------------------------------

const BASE_SINISTRO = (sinistroId) => `${BASE_EVENTO}/sinistri/${sinistroId}`;

/** TendinaEventiSinistroDTO: compatibili, periodo, nessunoDichiarato, consolidato, soloNessuno. */
export const fetchEventiAbbinabiliSinistro = (sinistroId) => async () =>
  esitoConStatus(() =>
    axiosInstance.get(`${BASE_SINISTRO(sinistroId)}/eventi-abbinabili`, { validateStatus: VALIDA_FINO_A_4XX }),
  );

/** "Nessun evento": il BE registra chi e quando e azzera il codice evento. */
export const dichiaraNessunEventoSinistro = (sinistroId) => async () =>
  esitoConStatus(() =>
    axiosInstance.put(`${BASE_SINISTRO(sinistroId)}/nessuno`, undefined, { validateStatus: VALIDA_FINO_A_4XX }),
  );

/** Ritira il "nessun evento": il sinistro torna abbinabile. */
export const revocaNessunEventoSinistro = (sinistroId) => async () =>
  esitoConStatus(() =>
    axiosInstance.delete(`${BASE_SINISTRO(sinistroId)}/nessuno`, { validateStatus: VALIDA_FINO_A_4XX }),
  );

// ---------------------------------------------------------------------------
// CSP-2788 T18 (fase C riassicurazione) - EventoCatastrofaleRiassicurazioneController del BFF (as-is da os-sinistri):
// dati riass dell'evento (R11-R13), lista riass dei sinistri con conferma/esclusione (R21-R22), tabella durate
// versionata (R15-R17) e scrittura dei domini (R14). Stessa forma d'esito delle liste: 400/403/404/409 col messaggio
// del BE da mostrare cosi' com'e'. Scrive solo il profilo riassicurazione (domini: anche il back-office): il FE
// nasconde le azioni, l'autorita' resta il BE (403).
// ---------------------------------------------------------------------------

const CON_4XX = { validateStatus: VALIDA_FINO_A_4XX };
const BASE_RIASS_EVENTO = (eventoId) => `${BASE_EVENTO}/${eventoId}/riassicurazione`;
const BASE_DURATE = `${BASE_EVENTO}/riassicurazione/durate`;
const BASE_DOMINI = `${BASE_EVENTO}/domini`;

/** DatiRiassEventoDTO (codice, date, avviso, calcolo); 404 evento inesistente. */
export const fetchDatiRiassEvento = (eventoId) => async () =>
  esitoConStatus(() => axiosInstance.get(BASE_RIASS_EVENTO(eventoId), CON_4XX));

/** PUT `{codiceEventoRiass, dataInizioEventoRiass, dataFineEventoRiass?}` -> DatiRiassEventoDTO aggiornato. */
export const salvaDatiRiassEvento = (eventoId, richiesta) => async () =>
  esitoConStatus(() => axiosInstance.put(BASE_RIASS_EVENTO(eventoId), richiesta, CON_4XX));

/** Proposta della data fine riass dalla tabella durate: EsitoCalcoloDurataDTO (CALCOLATA | NON_CALCOLABILE). */
export const calcolaDurataRiass = (tipoEvento, dataInizioRiass) => async () => {
  const query = new URLSearchParams({ tipoEvento: tipoEvento ?? '', dataInizioRiass: dataInizioRiass ?? '' });
  return esitoConStatus(() => axiosInstance.get(`${BASE_DURATE}/calcolo?${query.toString()}`, CON_4XX));
};

/** ListaSinistriRiassDTO: testata coi contatori e la pagina dei sinistri. */
export const fetchSinistriRiass = (eventoId, query) => async () =>
  esitoConStatus(() =>
    axiosInstance.get(`${BASE_RIASS_EVENTO(eventoId)}/sinistri${query ? `?${query}` : ''}`, CON_4XX),
  );

/** Conferma riass: `{sinistroIds}` o `{tutti: true}` -> EsitoSelezioneRiassDTO; 409 se mancano i dati riass. */
export const confermaSinistriRiass = (eventoId, selezione) => async () =>
  esitoConStatus(() => axiosInstance.put(`${BASE_RIASS_EVENTO(eventoId)}/sinistri/conferma`, selezione, CON_4XX));

/** Rimozione del flag di conferma: come la conferma; `tutti` = tutti i confermati dell'evento. */
export const escludiSinistriRiass = (eventoId, selezione) => async () =>
  esitoConStatus(() => axiosInstance.put(`${BASE_RIASS_EVENTO(eventoId)}/sinistri/esclusione`, selezione, CON_4XX));

/** Versioni della tabella durate (VersioneDurateDTO[]), in ordine cronologico. */
export const fetchVersioniDurate = () => async () => esitoConStatus(() => axiosInstance.get(BASE_DURATE, CON_4XX));

/** Nuova versione `{dataInizioValidita, righe}`: 201 / 400 / 409. */
export const creaVersioneDurate = (richiesta) => async () =>
  esitoConStatus(() => axiosInstance.post(BASE_DURATE, richiesta, CON_4XX));

/** Righe di una versione non ancora in vigore (409 sulle altre). */
export const modificaVersioneDurate = (versione, righe) => async () =>
  esitoConStatus(() => axiosInstance.put(`${BASE_DURATE}/${versione}`, { righe }, CON_4XX));

/** Sposta il confine con la versione successiva: `{dataFineValidita}` -> VersioneDurateDTO[] aggiornate. */
export const chiudiVersioneDurate = (versione, richiesta) => async () =>
  esitoConStatus(() => axiosInstance.put(`${BASE_DURATE}/${versione}/chiusura`, richiesta, CON_4XX));

/** Tutti i domini, anche i disattivati (senza `attivo`), per la gestione. */
export const fetchDominiGestione = () => async () => esitoConStatus(() => axiosInstance.get(BASE_DOMINI, CON_4XX));

export const creaDominioEvento = (richiesta) => async () =>
  esitoConStatus(() => axiosInstance.post(BASE_DOMINI, richiesta, CON_4XX));

export const modificaDominioEvento = (id, richiesta) => async () =>
  esitoConStatus(() => axiosInstance.put(`${BASE_DOMINI}/${id}`, richiesta, CON_4XX));

/** DELETE = disattivazione (il dominio resta, `attivo` false). */
export const disattivaDominioEvento = (id) => async () =>
  esitoConStatus(() => axiosInstance.delete(`${BASE_DOMINI}/${id}`, CON_4XX));

// ---------------------------------------------------------------------------
// Catena v1 per id_task (ListaSinistri / GrigliaSinistri), ripristinata accanto alla v2 (CSP-2788, decisione
// dell'utente: non si cancella codice che altri possono chiamare). La vista eventi monta SinistriEvento (v2); questi
// thunk restano per chi monta ListaSinistri. Rotte BFF con lo stesso contratto di HEAD (DenunciaController):
// GET /denunce/sinitri/eventoCatastrofale/{id}/lob/{lob} e PUT /denunce/consolidaEvento. R48: PUT
// /denunce/associaEvento e /disassociaEvento rimosse da os-sinistri e dal BFF (si associa e disassocia dalla v2).
// ---------------------------------------------------------------------------

/**
 * Il gate della SP confronta l'id con DUE forme: CONVERT(varchar, tch_codint)
 * (numerico) oppure tnm.TaskId (prefissato). Si mandano entrambe, ma la forma
 * numerica si ricava SOLO dal suffisso dopo l'ultimo '-' e solo se e' fatto
 * di sole cifre: una regex \d+ generica su un UUID (es. l'id di un evento,
 * 43f7d78b-...) estrarrebbe "43", che potrebbe combaciare con il tch_codint
 * di un task qualunque e far comparire in griglia righe che non c'entrano.
 * Meglio nessuna riga che righe sbagliate.
 */
function idPerGateSp(sinistro) {
  const grezzo = sinistro?.id;
  if (!grezzo) return [];
  const testo = String(grezzo);
  const suffisso = testo.split('-').pop();
  // il suffisso vuoto (id che finisce con '-') non passa la regex
  return suffisso !== testo && /^\d+$/.test(suffisso) ? [testo, suffisso] : [testo];
}

// Gli id viaggiano in query string come CSV: un evento con molti sinistri
// supererebbe il limite di lunghezza dell'URL (414). Si spezza in blocchi e
// si concatenano i risultati. size = dimensione del blocco, perche' serve
// l'intero blocco e non i primi 50.
// 100 id per blocco: ogni id ne produce fino a due (grezzo + numerico).
const CHUNK_SIZE = 100;

function blocchiDiId(ids) {
  const chunks = [];
  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + CHUNK_SIZE));
  }
  return chunks;
}

function righeDellaRisposta(res) {
  if (Array.isArray(res?.data?.content)) return res.data.content;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

async function righeTaskDelBlocco(chunk) {
  const params = new URLSearchParams({
    idTask: chunk.join(','),
    page: '0',
    size: String(chunk.length),
  });
  const API_URI_CALL = `${API_URL}/api/v1/bff/task/lista-lavorabili/search?${params.toString()}`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);
  const [res, err] = await executor.executeAsync(fetchData);
  if (err) {
    console.warn('fetchTaskCatastrofaliByIds failed', err);
    return [];
  }
  return righeDellaRisposta(res);
}

/**
 * Recupera dal BE le righe task dei sinistri di un evento catastrofale.
 *
 * <p>OSSQ-2381: la griglia dei sinistri catastrofali otteneva le colonne
 * incrociando la lista dell'evento con ticketsLavorabili. Da quando la lista
 * lavorabili e' paginata server-side quello stato contiene solo la pagina
 * corrente (50 righe), quindi i sinistri fuori pagina sparivano dalla griglia
 * anche se il BE li restituiva. Qui si chiedono esplicitamente al BE per id,
 * usando il filtro idTask della ricerca ibrida (CSV, gate lato SP).
 *
 * @param {Array} sinistri lista sinistri dell'evento (serve solo il campo id)
 */
export const fetchTaskCatastrofaliByIds = (sinistri) => async (dispatch) => {
  const ids = (sinistri || []).flatMap(idPerGateSp);
  if (ids.length === 0) {
    return dispatch(setTaskCatastrofali([]));
  }
  const risultati = await Promise.all(blocchiDiId(ids).map(righeTaskDelBlocco));
  return dispatch(setTaskCatastrofali(risultati.flat()));
};

// Cambiando evento in fretta partono piu' GET: solo l'ultima aggiorna lista e loader (come fetchListaBozze).
let ultimaRichiestaSinistriCatastrofali = 0;

const eventoAssente = (idEvento) => [null, undefined, ''].includes(idEvento);

/**
 * Sinistri lavorabili dell'evento per la griglia v1 (righe per id_task, filtrate dal BFF sull'insieme completo dei
 * task dell'utente). Esito come la v2 (`{ esito, status, data, messaggio }`): chi monta la lista mostra il messaggio.
 *
 * @param {string} idEvento id dell'evento
 * @param {boolean} isCodiceLob true per il Claims Coordinator (responsabilelob); nel path va sempre true/false
 */
export const fetchListaSinistriCatastrofali = (idEvento, isCodiceLob) => async (dispatch) => {
  ultimaRichiestaSinistriCatastrofali += 1;
  const richiesta = ultimaRichiestaSinistriCatastrofali;
  dispatch(setLoaderGrigliaSinistri(true));
  dispatch(setListaSinistriCatastrofali([]));

  if (eventoAssente(idEvento)) {
    dispatch(setLoaderGrigliaSinistri(false));
    return { esito: true, status: null, data: [], messaggio: null };
  }

  const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/sinitri/eventoCatastrofale/${idEvento}/lob/${Boolean(isCodiceLob)}`;
  const risultato = await esitoConStatus(() =>
    axiosInstance.get(API_URI_CALL, { validateStatus: VALIDA_FINO_A_4XX }),
  );
  if (richiesta !== ultimaRichiestaSinistriCatastrofali) return { ...risultato, superata: true };

  dispatch(setLoaderGrigliaSinistri(false));
  if (!risultato.esito) return risultato;
  const sinistri = Array.isArray(risultato.data) ? risultato.data : [];
  dispatch(setListaSinistriCatastrofali(sinistri));
  // Le righe task servono per le colonne della griglia: si chiedono al BE
  // per id, invece di cercarle nella pagina corrente dei lavorabili.
  dispatch(fetchTaskCatastrofaliByIds(sinistri));
  return risultato;
};

/** Azione della lista v1 -> rotta di DenunciaController del BFF (R48: resta il solo consolida). */
export const AZIONI_EVENTO_V1 = Object.freeze({
  consolidare: 'consolidaEvento',
});

/**
 * Consolida per id_task (contratto di HEAD, prima inline in ListaSinistri). Il BFF risponde con un `requestId` (esito
 * asincrono via toast); i rifiuti tornano con status e messaggio del BE. Un'azione che non c'e' (anche le vecchie
 * 'associare' e 'disassociare', R48) non chiama nulla ed esce con esito negativo.
 *
 * @param {string} tipo 'consolidare'
 * @param {string} eventoId id dell'evento (il consolida non lo manda: la firma resta quella della lista v1)
 * @param {Array<string>} listaTask id numerici dei task selezionati
 */
export const modificaEventoSinistriV1 = (tipo, eventoId, listaTask) => async () => {
  const rotta = AZIONI_EVENTO_V1[tipo];
  if (!rotta) return { esito: false, status: null, data: null, messaggio: null };
  return esitoConStatus(() =>
    axiosInstance.put(`${API_URL}/api/v1/bff/denunce/${rotta}`, listaTask, { validateStatus: VALIDA_FINO_A_4XX }),
  );
};

export default EventiCatastrofaliSlice.reducer;
